// src/controllers/customerController.js
import apiService from '../services/apiService';

const unwrap = (r) => {
  if (!r) return {};
  if ('success' in r) return r;
  if (r.data && 'success' in r.data) return r.data;
  return r;
};

// ── EMI Calculator (frontend) ─────────────────────────────────────────────────
export const calculateEMI = (plan) => {
  const { productPrice = 0, downPayment = 0, interestRate = 0,
          tenure = 1, tenureType = 'monthly', customDays = 30 } = plan;
  const principal = productPrice - downPayment;
  if (principal <= 0) return { emiAmount: 0, totalPayable: downPayment, totalInterest: 0 };

  if (interestRate === 0) {
    const emi = +(principal / tenure).toFixed(2);
    return { emiAmount: emi, totalPayable: +(downPayment + principal).toFixed(2), totalInterest: 0 };
  }

  const ppy = tenureType==='weekly' ? 52 : tenureType==='yearly' ? 1 : tenureType==='custom' ? Math.round(365/(customDays||30)) : 12;
  const r   = (interestRate / 100) / ppy;
  const n   = tenure;
  const emi = principal * r * Math.pow(1+r,n) / (Math.pow(1+r,n)-1);
  const tp  = +(downPayment + emi * n).toFixed(2);
  return { emiAmount: +emi.toFixed(2), totalPayable: tp, totalInterest: +(tp - productPrice).toFixed(2) };
};

export const generateSchedule = (plan) => {
  const { emiAmount, totalPayable, totalInterest } = calculateEMI(plan);
  const { tenure=1, tenureType='monthly', customDays=30, startDate, downPayment=0, productPrice=0, interestRate=0 } = plan;
  const days  = tenureType==='weekly' ? 7 : tenureType==='yearly' ? 365 : tenureType==='custom' ? (customDays||30) : 30;
  const start = new Date(startDate || Date.now());
  let bal = productPrice - downPayment;
  const payments = [];
  for (let i = 1; i <= tenure; i++) {
    const due = new Date(start); due.setDate(due.getDate() + days * i);
    const interest  = +(bal * (interestRate/100) / 12).toFixed(2);
    const principal = +(emiAmount - interest).toFixed(2);
    bal = +Math.max(0, bal - principal).toFixed(2);
    payments.push({ installmentNo:i, dueDate:due.toISOString().split('T')[0], emiAmount, principal, interest, balance:bal, status:'Pending', paidAmount:0, paidDate:null, notes:'' });
  }
  return { emiAmount, totalPayable, totalInterest, payments };
};

// ── Controller ────────────────────────────────────────────────────────────────
export const customerController = {

  getAll: async (filters = {}) => {
    try {
      const qp  = new URLSearchParams(filters).toString();
      const raw = unwrap(await apiService.get(qp ? `customers?${qp}` : 'customers'));
      return { success: true, data: raw.data || [], total: raw.total || 0, totalPages: raw.totalPages || 1 };
    } catch (e) {
      return { success: false, data: [], message: e?.data?.message || e.message };
    }
  },

  getById: async (id) => {
    try {
      const raw = unwrap(await apiService.get(`customers/${id}`));
      return { success: true, data: raw.data || raw };
    } catch (e) {
      return { success: false, data: null, message: e?.data?.message || e.message };
    }
  },

  // Create with optional file uploads
  create: async (data, files = {}) => {
    try {
      const fd = new FormData();

      // Scalar fields
      const SCALAR = ['fullName','phoneNumber','alternatePhone','email','address','city','state','pincode','aadharNumber','panNumber','notes','imei1','imei2','digitalSignature','signedAt'];
      SCALAR.forEach(k => { if (data[k] != null && data[k] !== '') fd.append(k, String(data[k])); });

      // EMI Plan
      if (data.emiPlan) fd.append('emiPlan', JSON.stringify(data.emiPlan));

      // Files
      if (files.profileImage)     fd.append('profileImage',     files.profileImage);
      if (files.aadharCard)       fd.append('aadharCard',       files.aadharCard);
      if (files.panCard)          fd.append('panCard',          files.panCard);
      if (files.digitalSignature) fd.append('digitalSignature', files.digitalSignature);

      const raw = unwrap(await apiService.postFormData('customers', fd));
      if (raw.success === false) return { success: false, message: raw.message, errors: raw.errors || [] };
      return { success: true, data: raw.data, message: raw.message || 'Customer created' };
    } catch (e) {
      return { success: false, message: e?.data?.message || e.message };
    }
  },

  update: async (id, data, files = {}) => {
    try {
      const fd = new FormData();
      const SCALAR = ['fullName','phoneNumber','alternatePhone','email','address','city','state','pincode','aadharNumber','panNumber','notes','imei1','imei2','digitalSignature','signedAt'];
      SCALAR.forEach(k => { if (data[k] != null && data[k] !== '') fd.append(k, String(data[k])); });
      if (data.emiPlan) fd.append('emiPlan', JSON.stringify(data.emiPlan));
      if (files.profileImage)     fd.append('profileImage',     files.profileImage);
      if (files.aadharCard)       fd.append('aadharCard',       files.aadharCard);
      if (files.panCard)          fd.append('panCard',          files.panCard);
      if (files.digitalSignature) fd.append('digitalSignature', files.digitalSignature);

      const raw = unwrap(await apiService.putFormData(`customers/${id}`, fd));
      if (raw.success === false) return { success: false, message: raw.message };
      return { success: true, data: raw.data, message: raw.message || 'Customer updated' };
    } catch (e) {
      return { success: false, message: e?.data?.message || e.message };
    }
  },

  remove: async (id) => {
    try {
      const raw = unwrap(await apiService.delete(`customers/${id}`));
      return { success: true, message: raw.message || 'Customer deleted' };
    } catch (e) {
      return { success: false, message: e?.data?.message || e.message };
    }
  },

  regenerateEMI: async (id, planData) => {
    try {
      const raw = unwrap(await apiService.post(`customers/${id}/emi/regenerate`, planData));
      return { success: true, data: raw.data, message: raw.message };
    } catch (e) {
      return { success: false, message: e?.data?.message || e.message };
    }
  },

  updatePayment: async (id, paymentData) => {
    try {
      const raw = unwrap(await apiService.patch(`customers/${id}/emi/payment`, paymentData));
      return { success: true, data: raw.data, message: raw.message };
    } catch (e) {
      return { success: false, message: e?.data?.message || e.message };
    }
  },

  markOverdue: async (id, installmentNo) => {
    try {
      const raw = unwrap(await apiService.patch(`customers/${id}/emi/payment/overdue`, { installmentNo }));
      return { success: true, message: raw.message };
    } catch (e) {
      return { success: false, message: e?.data?.message || e.message };
    }
  },

  getStatistics: async () => {
    try {
      const raw = unwrap(await apiService.get('customers/statistics'));
      return { success: true, data: raw.data };
    } catch (e) {
      return { success: false, data: null, message: e.message };
    }
  },
};

export default customerController;